/**
 * /dashboard/people (§2.6). The directory (screenshots/18) with the top of the page for the viewer's group
 * (§2.6 "People tab by user group"): resident (21), alderperson's office (22), lobbyist / advocacy (23).
 * Paints from snapshot:people (hydrated in main.tsx before render); the live feed opens after paint.
 */
import { useEffect, useState } from "react";
import { Download, Info, Play } from "lucide-react";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import { can, type OrgRole } from "../nav/navConfig";
import { peopleApi, peopleStore, usePeople } from "./peopleStore";
import { DirectoryFilters, PeopleTable, SelectionBar, TableFooter, TypeTabs } from "./Directory";
import { OfficeOverview, OfficeToolbar, type OfficeFilter } from "./OfficeOverview";
import { AdvocacyOverview, ResidentOverview } from "./GroupOverviews";
import { openPeopleFeed, type TestimonyLive } from "./liveFeed";
import { meetingHref } from "./atoms";
import { HowEstimated } from "./LeaningCard";

export function PeoplePage({ role }: { role: OrgRole | null }) {
  const group = usePeople((s) => s.group);
  const office = usePeople((s) => s.office);
  const ward = usePeople((s) => s.query.ward);
  const [alert, setAlert] = useState<TestimonyLive | null>(null);
  const [how, setHow] = useState(false);
  useEffect(() => openPeopleFeed((e) => {
    const o = peopleStore.getState().office;
    if (o && o.testimonyAlerts && e.ward === o.ward && can("office", role, "constituents")) setAlert(e);
  }), [role]);

  const canExport = can(group, role, "csvExport");
  return (
    <div className="people-page">
      <header className="page-header">
        <div className="page-header__title">
          <div className="title-row"><h1 className="page-title">People</h1><ConnectionStatus /></div>
        </div>
        <div className="row-gap">
          <button className="btn" onClick={() => setHow(true)}><Info size={14} aria-hidden />How leaning is estimated</button>
          {canExport && group !== "resident" && <button className="btn" onClick={() => { location.href = `/api/dashboard/people/export.csv?${exportParams()}`; }}><Download size={14} aria-hidden />Export CSV</button>}
        </div>
      </header>

      {alert && office && (
        <div className="p-live-alert" role="status">
          <i className="p-live-alert__dot" aria-hidden />
          <span><b>A Ward {office.ward} resident is speaking</b><small>{alert.body.replace(/^Committee on (the )?/, "")} · public comment · live</small></span>
          <a className="btn" href={meetingHref(alert.meetingId, alert.t)}><Play size={12} aria-hidden />Join here</a>
        </div>
      )}

      {group === "resident" && <ResidentOverview />}
      {group === "advocacy" && <AdvocacyOverview canTarget={can(group, role, "targetLists")} canBrief={can(group, role, "briefMe")} />}
      {group === "office" && office ? <OfficeSection role={role} /> : group !== "advocacy" && (
        <>
          <div className="p-dir">
            <DirectoryFilters wardActive={group === "resident"} />
            <TypeTabs />
          </div>
          <PeopleTable />
          <TableFooter label={group === "resident" && ward != null
            ? `Showing {shown} of {total} people from Ward ${ward}`
            : "Showing {shown} of {total} · Portraits are official photos; rectangles are video frames at the time of comment."} />
        </>
      )}
      {group !== "advocacy" && <SelectionBar canTag={can(group, role, "personNotes")} canExport={canExport} />}
      {how && <HowEstimated onClose={() => setHow(false)} />}
    </div>
  );
}

function OfficeSection({ role }: { role: OrgRole | null }) {
  const office = usePeople((s) => s.office)!;
  const filter = usePeople((s) => s.query.office ?? "everyone");
  const sort = usePeople((s) => s.query.sort);
  const [q, setQ] = useState(usePeople((s) => s.query.q));
  useEffect(() => { const t = setTimeout(() => q !== peopleStore.getState().query.q && peopleStore.getState().setQuery({ q }), 200); return () => clearTimeout(t); }, [q]);
  const onFilter = (f: OfficeFilter) => peopleStore.getState().setQuery({ office: f === filter && f !== "everyone" ? "everyone" : f });
  const onAlerts = async (on: boolean) => {
    peopleStore.setState({ office: { ...office, testimonyAlerts: on } });
    await peopleApi("alerts", { method: "PUT", body: JSON.stringify({ testimonyAlerts: on }) }).catch(() => peopleStore.setState({ office: { ...office } }));
  };
  const nextSort = { recent: "active", active: "name", name: "recent" } as const;
  return (
    <>
      <OfficeOverview data={office} filter={filter} onFilter={onFilter} onToggleAlerts={onAlerts} canToggleAlerts={role === "owner" || role === "admin"} />
      <OfficeToolbar data={office} filter={filter} onFilter={onFilter} q={q} onQ={setQ} sort={sort} onSort={() => peopleStore.getState().setQuery({ sort: nextSort[sort] })} />
      <PeopleTable office />
      <TableFooter label="Showing {shown} of {total}" />
    </>
  );
}

function exportParams() {
  const q = peopleStore.getState().query;
  const p = new URLSearchParams();
  if (q.type !== "all") p.set("type", q.type);
  if (q.q) p.set("q", q.q);
  if (q.ward != null) p.set("ward", String(q.ward));
  if (q.area) p.set("area", q.area);
  if (q.leaning) p.set("leaning", q.leaning);
  p.set("sort", q.sort);
  if (q.office) p.set("office", q.office);
  return p.toString();
}

